import React, { createContext, useContext } from "react";
import type { ServiceCategory } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { useArchivedServices } from "../hooks/use-archived-services";
import { useCategorySubscription } from "../hooks/use-category-subscription";
import { useServiceSubscription } from "../hooks/use-service-subscription";
import { useServices } from "../hooks/use-services";
import { useServicesUIStore } from "../store/use-services-ui-store";
import type { CategorySortUpdate, ServiceRowData, ServiceSortUpdate } from "../types";

interface ServicesContextValue {
  currentSalon: { id: string } | null;
  filteredCategories: ServiceCategory[];
  filteredServicesByCategory: Record<string, ServiceRowData[]>;
  archivedCategories: ServiceCategory[];
  archivedServicesByCategory: Record<string, ServiceRowData[]>;
  allArchivedServices: any[];
  totalServices: number;
  totalCategories: number;
  archivedCategoriesCount: number;
  archivedServicesCount: number;
  hasArchivedItems: boolean;
  totalArchivedCount: number;
  loading: boolean;
  error: any;
  retry: () => void;
  updateCategorySortOrder: (updates: CategorySortUpdate[]) => Promise<void>;
  updateServiceSortOrder: (updates: ServiceSortUpdate[]) => Promise<void>;
  isUpdatingSortOrder: boolean;
  isUpdatingServiceSortOrder: boolean;
}

const ServicesContext = createContext<ServicesContextValue | null>(null);

export function ServicesProvider({ children }: { children: React.ReactNode }) {
  const { currentSalon } = useSalonStore();

  // Subscribe to real-time service and category updates
  useServiceSubscription(currentSalon?.id);
  useCategorySubscription(currentSalon?.id);

  const servicesData = useServices({
    salonId: currentSalon?.id || undefined,
    filters: {
      searchTerm: useServicesUIStore((state) => state.searchTerm),
    },
  });

  const archivedData = useArchivedServices({
    salonId: currentSalon?.id || null,
  });

  const combinedData = {
    ...servicesData,
    archivedCategories: archivedData.archivedCategories,
    archivedServicesByCategory: archivedData.archivedServicesByCategory,
    allArchivedServices: archivedData.allArchivedServices,
    archivedCategoriesCount: archivedData.archivedCategoriesCount,
    archivedServicesCount: archivedData.archivedServicesCount,
    hasArchivedItems: archivedData.hasArchivedItems,
    totalArchivedCount: archivedData.totalArchivedCount,
    currentSalon,
  };

  return <ServicesContext.Provider value={combinedData}>{children}</ServicesContext.Provider>;
}

export function useServicesContext() {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error("useServicesContext must be used within ServicesProvider");
  }
  return context;
}
